import Api from '@sendsprint/api-types';
import { PaymentMethodsI } from '@src/components/paymentDropdownList/components/getPaymentMethods';
import { CONFIG_NO_ERROR_TOAST, DURATION_10_MIN, DURATION_4_MIN } from '@src/constants';
import {
  changePasswordFromProfileService,
  changePasswordService,
  generateOtpService,
  getIdIsActiveService,
  getSignUpSourceService,
  getVeriffStatusService,
  loadChargeRouteService,
  loginService,
  loginUserService,
  logoutService,
  refreshTokenService,
  registerService,
  resetPasswordByEmailService,
  setAddressService,
  submitUserCategoryService,
  veriffSessionsService,
  veriffSessionsTestService,
  verifyOtpService
} from '@src/services/auth-service';
import ClientApi from '@src/types/client';
import { AxiosError, AxiosResponse } from 'axios';
import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from 'react-query';

export const useLogin = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.General.LoggedInUser,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Account.Login.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.General.LoggedInUser,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Account.Login.Request
  >('login', loginService, options);
};

export const useLoginUser = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.Account.LoginUser.Response,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Account.LoginUser.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.Account.LoginUser.Response,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Account.LoginUser.Request
  >('loginUser', loginUserService, options);
};

export const useRegister = (
  options?: Omit<
    UseMutationOptions<
      [Api.Model.User],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Account.Register.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    [Api.Model.User],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Account.Register.Request
  >('register', registerService, options);
};

export const useSubmitUserCategory = (
  options?: Omit<
    UseMutationOptions<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      boolean,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Account.SubmitUserCategory.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    boolean,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Account.SubmitUserCategory.Request
  >('submitUserCategory', submitUserCategoryService, options);
};

export const useRefreshToken = (
  payload: ClientApi.Account.RefreshToken.Request,
  options?: UseQueryOptions<
    ClientApi.General.TokenDetails,
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  return useQuery<ClientApi.General.TokenDetails, AxiosError<Api.Endpoint.Response.ErrorResponse>>({
    queryKey: ['refreshToken'],
    queryFn: () => refreshTokenService(payload, CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_4_MIN,
    ...options
  });
};

export const useLogout = (
  options?: Omit<
    UseMutationOptions<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      boolean,
      AxiosError<Api.Endpoint.Response.ErrorResponse>
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    boolean,
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >('logout', logoutService, options);
};

export const useSignUpSources = (
  options?: UseQueryOptions<
    ClientApi.Account.SignUpSource.SignUpSourceData[],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  return useQuery<
    ClientApi.Account.SignUpSource.SignUpSourceData[],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >({
    queryKey: ['getSignUpSource'],
    queryFn: () => getSignUpSourceService(CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_10_MIN,
    ...options
  });
};

export const useLoadChargeRoute = (
  payload: ClientApi.Account.LoadChargeRoute.Request,
  options?: UseQueryOptions<PaymentMethodsI[], AxiosError<Api.Endpoint.Response.ErrorResponse>>
) => {
  return useQuery<PaymentMethodsI[], AxiosError<Api.Endpoint.Response.ErrorResponse>>({
    queryKey: ['loadChargeRoute', payload.country],
    queryFn: () => loadChargeRouteService(payload, CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_10_MIN,
    ...options
  });
};

export const useGenerateOtp = (
  options?: Omit<
    UseMutationOptions<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      AxiosResponse<Api.Endpoint.Response.SuccessResponse, any>,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Account.GenerateOtp.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosResponse<Api.Endpoint.Response.SuccessResponse, any>,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Account.GenerateOtp.Request
  >('generateOtp', generateOtpService, options);
};

export const useVerifyOtp = (
  options?: Omit<
    UseMutationOptions<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      AxiosResponse<Api.Endpoint.Response.SuccessResponse, any>,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Account.VerifyOtp.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosResponse<Api.Endpoint.Response.SuccessResponse, any>,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Account.VerifyOtp.Request
  >('verifyOtp', verifyOtpService, options);
};

export const useSetAddress = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.General.UserAddress,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Account.SetAddress.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.General.UserAddress,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Account.SetAddress.Request
  >('setAddress', setAddressService, options);
};

export const useResetPasswordByEmail = (
  options?: Omit<
    UseMutationOptions<
      Api.Account.ResetPasswordByEmail.Response,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Account.ResetPasswordByEmail.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    Api.Account.ResetPasswordByEmail.Response,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Account.ResetPasswordByEmail.Request
  >('resetPasswordByEmail', resetPasswordByEmailService, options);
};

export const useChangePassword = (
  options?: Omit<
    UseMutationOptions<
      Api.Account.ChangePassword.Response,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Account.ChangePassword.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    Api.Account.ChangePassword.Response,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Account.ChangePassword.Request
  >('changePassword', changePasswordService, options);
};

export const useChangePasswordFromProfile = (
  options?: Omit<
    UseMutationOptions<
      Api.Account.ChangePasswordFromProfile.Response,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Account.ChangePasswordFromProfile.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    Api.Account.ChangePasswordFromProfile.Response,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Account.ChangePasswordFromProfile.Request
  >('changePasswordFromProfile', changePasswordFromProfileService, options);
};

export const useVeriffStatus = (
  payload: ClientApi.Account.GetVeriffStatus.Request,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseQueryOptions<any, AxiosError<Api.Endpoint.Response.ErrorResponse>>
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useQuery<any, AxiosError<Api.Endpoint.Response.ErrorResponse>>({
    queryKey: ['getVeriffStatus', payload.email],
    queryFn: () => getVeriffStatusService(payload, CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_10_MIN,
    ...options
  });
};

export const useVeriffSessions = (
  options?: Omit<
    UseMutationOptions<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Account.VeriffSessions.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Account.VeriffSessions.Request
  >('veriffSessions', veriffSessionsService, options);
};

export const useVeriffSessionsTest = (
  options?: Omit<
    UseMutationOptions<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Account.VeriffSessionsTest.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Account.VeriffSessionsTest.Request
  >('veriffSessionsTest', veriffSessionsTestService, options);
};

export const useGetIdIsActive = (
  options?: UseQueryOptions<
    ClientApi.Account.IdIsActive.Response,
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  return useQuery<
    ClientApi.Account.IdIsActive.Response,
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >({
    queryKey: ['getIdIsActive'],
    queryFn: () => getIdIsActiveService(CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_10_MIN,
    ...options
  });
};
